import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { changeAppLanguage, getCurrentAppLocale, LOCALE_REGISTRY, persistLocalePreference, VISIBLE_LANGUAGE_SELECTOR_LOCALES } from '../i18n';
import { usersAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { brandClasses } from '../theme';

interface LanguageSelectorProps {
  compact?: boolean;
}

export const LanguageSelector: React.FC<LanguageSelectorProps> = ({ compact = false }) => {
  const { t } = useTranslation();
  const { user, updateCurrentUser } = useAuth();
  const [selectedLocale, setSelectedLocale] = useState(() => getCurrentAppLocale());
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setSelectedLocale(getCurrentAppLocale());
  }, [user?.preferredLocale]);

  const handleChange = async (event: React.ChangeEvent<HTMLSelectElement>) => {
    const nextLocale = event.target.value;
    setIsSaving(true);

    try {
      const resolvedLocale = persistLocalePreference(nextLocale);
      await changeAppLanguage(resolvedLocale);
      setSelectedLocale(resolvedLocale);

      if (user) {
        await usersAPI.updateProfile({ preferredLocale: resolvedLocale });
        updateCurrentUser({ preferredLocale: resolvedLocale });
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <label className={`flex ${compact ? 'items-center gap-2' : 'flex-col gap-1'}`}>
      {!compact && (
        <span className={`text-xs font-semibold uppercase tracking-[0.18em] ${brandClasses.subtleText}`}>
          {t('labels.language')}
        </span>
      )}
      <select
        aria-label={t('labels.language')}
        value={selectedLocale}
        onChange={handleChange}
        disabled={isSaving}
        className={`rounded-xl border px-3 py-2 text-sm ${brandClasses.warmSurface} ${brandClasses.softBorder} ${brandClasses.primaryText} ${compact ? 'min-w-[150px]' : 'min-w-[220px]'}`}
      >
        {VISIBLE_LANGUAGE_SELECTOR_LOCALES.map((locale) => (
          <option key={locale} value={locale}>
            {LOCALE_REGISTRY[locale].nativeName}
          </option>
        ))}
      </select>
    </label>
  );
};
